import React, { createContext, useContext, useState, useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { HelmetProvider } from "react-helmet-async";

// Pages — eager-loaded (small, common, or always-needed for first paint)
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";

// Auth + Protected app pages — lazy-loaded (only fetched when user navigates)
const Home = lazy(() => import("@/pages/Home"));
const Jobs = lazy(() => import("@/pages/Jobs"));
const Network = lazy(() => import("@/pages/Network"));
const Inbox = lazy(() => import("@/pages/Inbox"));
const Settings = lazy(() => import("@/pages/Settings"));
const LinkedInConnect = lazy(() => import("@/pages/LinkedInConnect"));
const Templates = lazy(() => import("@/pages/Templates"));
const DiscoverJobs = lazy(() => import("@/pages/DiscoverJobs"));
const Analytics = lazy(() => import("@/pages/Analytics"));
const TaskActivity = lazy(() => import("@/pages/TaskActivity"));
const Autopilot = lazy(() => import("@/pages/Autopilot"));
const AdminPanel = lazy(() => import("@/pages/AdminPanel"));
const Terms = lazy(() => import("@/pages/Terms"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Refund = lazy(() => import("@/pages/Refund"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const Billing = lazy(() => import("@/pages/Billing"));
const Checkout = lazy(() => import("@/pages/Checkout"));

// Programmatic SEO pages — lazy-loaded (large, only fetched per route)
const ReferIndex = lazy(() => import("@/pages/ReferIndex"));
const ReferCompanyPage = lazy(() => import("@/pages/ReferCompanyPage"));
const VsCompetitorPage = lazy(() => import("@/pages/VsCompetitorPage"));
const JobsIndex = lazy(() => import("@/pages/JobsIndex"));
const JobRoleCompanyPage = lazy(() => import("@/pages/JobRoleCompanyPage"));
const BlogIndex = lazy(() => import("@/pages/BlogIndex"));
const BlogPostPage = lazy(() => import("@/pages/BlogPostPage"));
const LearnIndex = lazy(() => import("@/pages/LearnIndex"));
const LearnStepPage = lazy(() => import("@/pages/LearnStepPage"));
const ReferralTemplatesPublic = lazy(() => import("@/pages/ReferralTemplatesPublic"));
const ResignationLetterPage = lazy(() => import("@/pages/ResignationLetterPage"));
const ResignationLetterIndex = lazy(() => import("@/pages/ResignationLetterPage").then(m => ({ default: m.ResignationLetterIndex })));
const ResumeHeadlinePage = lazy(() => import("@/pages/ResumeHeadlinePage"));
const ResumeHeadlineIndex = lazy(() => import("@/pages/ResumeHeadlinePage").then(m => ({ default: m.ResumeHeadlineIndex })));
const AlternativesIndex = lazy(() => import("@/pages/AlternativesIndex"));
const LiveDashboard = lazy(() => import("@/pages/LiveDashboard"));
const ToolsIndex = lazy(() => import("@/pages/ToolsIndex"));
const ProductDemoWatch = lazy(() => import("@/pages/ProductDemoWatch"));

// Demo OS — lazy-loaded (separate user journey, never needed on first paint)
const DemoLogin = lazy(() => import("@/demo/DemoLogin"));
const DemoLayout = lazy(() => import("@/demo/DemoLayout"));
const DemoCRM = lazy(() => import("@/demo/DemoCRM"));
const DemoLeadDetail = lazy(() => import("@/demo/DemoLeadDetail"));
const LiveDemo = lazy(() => import("@/demo/LiveDemo"));
const DemoSettings = lazy(() => import("@/demo/DemoSettings"));
const KitPage = lazy(() => import("@/pages/KitPage"));
const Onboard = lazy(() => import("@/pages/Onboard"));

import { NotFound } from "@/pages/ErrorPages";

// Layout
import AppLayout from "@/components/AppLayout";

import ErrorBoundary from "@/components/ErrorBoundary";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

// Safe localStorage wrapper (Safari private browsing throws on setItem)
function safeGetItem(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}
function safeSetItem(key, value) {
  try { localStorage.setItem(key, value); } catch {}
}
function safeRemoveItem(key) {
  try { localStorage.removeItem(key); } catch {}
}

// API instance
export const api = axios.create({ baseURL: API, headers: { "Content-Type": "application/json" } });

api.interceptors.request.use((config) => {
  const token = safeGetItem("chillrefer_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && error.config?.url?.includes("/auth/me")) {
      try {
        safeRemoveItem("chillrefer_token");
        safeRemoveItem("chillrefer_user");
      } catch (e) {}
      // Don't do hard redirect — let React handle it
    }
    return Promise.reject(error);
  }
);

function formatApiError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Note: document.title is managed per-route by react-helmet-async — do not
    // override here. Setting it would clobber the SEO-critical per-page title
    // (e.g. "How to Get a Referral at Google") on every public page.
    const init = async () => {
      const token = safeGetItem("chillrefer_token");
      if (token) {
        try {
          const res = await api.get("/auth/me");
          setUser(res.data);
          safeSetItem("chillrefer_user", JSON.stringify(res.data));

          // Show approval notification if pending
          if (res.data.pending_notification?.type === "account_approved") {
            setTimeout(() => {
              toast.success("Your account has been approved! You now have full access to ChillRefer.", { duration: 8000 });
              api.post("/auth/dismiss-notification").catch(() => {});
            }, 500);
          }
        } catch {
          safeRemoveItem("chillrefer_token");
          safeRemoveItem("chillrefer_user");
        }
      }
      setLoading(false);
    };
    init();
  }, []);

  const login = async (email, password) => {
    try {
      const res = await api.post("/auth/login", { email, password });
      const { access_token } = res.data;
      safeSetItem("chillrefer_token", access_token);

      // Fetch full user data from /me (includes plan, email_verified)
      const meRes = await api.get("/auth/me");
      const fullUser = meRes.data;
      safeSetItem("chillrefer_user", JSON.stringify(fullUser));
      setUser(fullUser);

      // Check for approval notification
      if (fullUser.pending_notification?.type === "account_approved") {
        setTimeout(() => {
          toast.success("Your account has been approved! You now have full access to ChillRefer.", { duration: 8000 });
          api.post("/auth/dismiss-notification").catch(() => {});
        }, 1000);
      }

      return { success: true, user: fullUser };
    } catch (e) {
      return { success: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const register = async (name, email, password) => {
    try {
      const res = await api.post("/auth/register", { name, email, password });
      const { access_token, user: u } = res.data;
      safeSetItem("chillrefer_token", access_token);
      safeSetItem("chillrefer_user", JSON.stringify(u));
      setUser(u);
      return { success: true, user: u };
    } catch (e) {
      return { success: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const logout = () => {
    safeRemoveItem("chillrefer_token");
    safeRemoveItem("chillrefer_user");
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      safeSetItem("chillrefer_user", JSON.stringify(res.data));
      return res.data;
    } catch {}
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#09090b", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "16px" }}>
        <div style={{ width: "32px", height: "32px", border: "3px solid #FACC15", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        <p style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.8rem" }}>Loading...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();
  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#09090b", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "16px" }}>
        <div style={{ width: "32px", height: "32px", border: "3px solid #FACC15", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/app/home" replace />;
  return children;
};

const ProRoute = ({ children }) => {
  const { user } = useAuth();
  if (user?.plan !== "pro") return <Navigate to="/app/billing" replace />;
  return children;
};

function App() {
  return (
    <HelmetProvider>
    <ErrorBoundary>
    <AuthProvider>
      <BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            style: { background: "#09090b", color: "#fff", border: "1px solid rgba(255,255,255,0.1)" },
          }}
        />
        <Suspense fallback={
          <div style={{ minHeight: "100vh", background: "#09090b", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "16px" }}>
            <div style={{ width: "32px", height: "32px", border: "3px solid #FACC15", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        }>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/refund" element={<Refund />} />

          {/* Programmatic SEO pages — public, indexable */}
          <Route path="/refer" element={<ReferIndex />} />
          <Route path="/refer/:slug" element={<ReferCompanyPage />} />
          <Route path="/vs/:slug" element={<VsCompetitorPage />} />
          <Route path="/jobs" element={<JobsIndex />} />
          <Route path="/jobs/:combined" element={<JobRoleCompanyPage />} />
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/learn" element={<LearnIndex />} />
          <Route path="/learn/:slug" element={<LearnStepPage />} />
          <Route path="/templates" element={<ReferralTemplatesPublic />} />
          <Route path="/resignation-letter" element={<ResignationLetterIndex />} />
          <Route path="/resignation-letter/:slug" element={<ResignationLetterPage />} />
          <Route path="/resume-headline" element={<ResumeHeadlineIndex />} />
          <Route path="/resume-headline/:slug" element={<ResumeHeadlinePage />} />
          <Route path="/alternatives" element={<AlternativesIndex />} />
          <Route path="/alternatives/:slug" element={<VsCompetitorPage />} />
          <Route path="/live" element={<LiveDashboard />} />
          <Route path="/tools" element={<ToolsIndex />} />
          <Route path="/product-demo" element={<ProductDemoWatch />} />

          <Route path="/admin" element={<AdminRoute><AdminPanel /></AdminRoute>} />

          {/* Public Advocate Kit page — no auth, no app shell */}
          <Route path="/kit/:token" element={<KitPage />} />

          {/* Post-payment onboarding — discovery funnel */}
          <Route path="/onboard" element={<Onboard />} />

          {/* Demo OS — fully separate from /app shell */}
          <Route path="/demo" element={<DemoLogin />} />
          <Route path="/demo/*" element={<DemoLayout />}>
            <Route path="crm" element={<DemoCRM />} />
            <Route path="lead/:leadId" element={<DemoLeadDetail />} />
            <Route path="live/:leadId" element={<LiveDemo />} />
            <Route path="settings" element={<DemoSettings />} />
          </Route>

          <Route path="/app" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
            <Route index element={<Navigate to="/app/home" replace />} />
            <Route path="home" element={<Home />} />
            <Route path="discover" element={<ProRoute><DiscoverJobs /></ProRoute>} />
            <Route path="jobs" element={<ProRoute><Jobs /></ProRoute>} />
            <Route path="network" element={<ProRoute><Network /></ProRoute>} />
            <Route path="inbox" element={<ProRoute><Inbox /></ProRoute>} />
            <Route path="templates" element={<ProRoute><Templates /></ProRoute>} />
            <Route path="analytics" element={<ProRoute><Analytics /></ProRoute>} />
            <Route path="activity" element={<ProRoute><TaskActivity /></ProRoute>} />
            <Route path="autopilot" element={<ProRoute><Autopilot /></ProRoute>} />
            <Route path="settings" element={<Settings />} />
            <Route path="billing" element={<Billing />} />
            <Route path="linkedin-connect" element={<ProRoute><LinkedInConnect /></ProRoute>} />
          </Route>

          {/* Dedicated checkout — outside AppLayout for a focused conversion
              surface (no sidebar/bottom-nav distractions). Still auth-gated. */}
          <Route path="/app/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />

          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
    </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
